import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import { api, errorMessage } from "../api/client";
import { AsyncBlock } from "../components/AsyncBlock";
import { BranchPlanPanel } from "../components/BranchPlanPanel";
import { BranchSearch, matchesBranch } from "../components/BranchSearch";
import { CopyButton } from "../components/CopyButton";
import { Panel } from "../components/Panel";
import { Select } from "../components/Select";
import { formatDateTime } from "../lib/format";
import { ui } from "../lib/ui";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";
import type { FleetRow, IssuedToken } from "../types";

type Scope = "all" | "enrolled" | "unenrolled" | "stale";

interface NewBranch {
  id: string;
  name: string;
  governorate: string;
  area: string;
  address: string;
}

const EMPTY_BRANCH: NewBranch = { id: "", name: "", governorate: "", area: "", address: "" };

/** The store slugs in config/branches.json are the website's: lowercase,
 *  words joined by hyphens. A new store gets the same shape from its name. */
const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const STALE_HOURS = 26; // a heartbeat is hourly; a day without one is news
const PAGE_SIZE = 25; // 462 branches; the table pages rather than scrolls

function isStale(row: FleetRow): boolean {
  if (!row.token_prefix) return false;
  if (!row.last_seen_at) return true;
  return Date.now() - new Date(row.last_seen_at).getTime() > STALE_HOURS * 3_600_000;
}

/** Whether a branch has sent anything the plan panel can show. */
const hasSetup = (row: FleetRow): boolean => row.has_plan || row.cameras > 0;

/** A fleet row as the shared branch search sees it. */
const searchable = (row: FleetRow) => ({
  id: row.branch_id,
  name: row.name,
  governorate: row.governorate,
  area: row.area,
  note: row.token_prefix ? null : "no token",
});

/** Every branch, whether a PC speaks for it, and what that PC last said.
 *  Tokens are issued here and shown exactly once, and the floor plan a PC
 *  synced is opened per row. */
export function Fleet() {
  const fleet = useAsync(() => api.fleet(), []);
  const [governorate, setGovernorate] = useState("");
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<NewBranch>(EMPTY_BRANCH);
  // Which branch's floor plan is open, in the URL so it is a shareable link.
  const [planId, setPlanId] = useState<string | null>(() => urlParam("plan"));
  // Table page, 0-based here and 1-based in the URL.
  const [page, setPage] = useState(() => Math.max(0, Number(urlParam("page") ?? 1) - 1));
  useUrlState({ plan: planId ?? "", page: page > 0 ? String(page + 1) : "" }, (p) => {
    if ("plan" in p) setPlanId(p.plan || null);
    if ("page" in p) setPage(Math.max(0, Number(p.page || 1) - 1));
  });
  const planPanel = useRef<HTMLDivElement>(null);
  const tableTop = useRef<HTMLDivElement>(null);

  const rows = fleet.data ?? [];
  const governorates = useMemo(
    () => Array.from(new Set(rows.map((r) => r.governorate))).sort(),
    [rows],
  );
  const enrolled = rows.filter((r) => r.token_prefix).length;
  const stale = rows.filter(isStale).length;
  // Kept off `visible` so filtering the table does not close the open plan.
  const planRow = planId ? rows.find((r) => r.branch_id === planId) ?? null : null;

  // The plan opens below the table, so a row far down would otherwise leave
  // it off screen. Waits for the panel itself — a link with ?plan= arrives
  // before the fleet has loaded — and follows it while the setup lands,
  // since scrolling to an empty box stops short of where it ends up.
  useEffect(() => {
    const panel = planPanel.current;
    if (!planRow || !panel) return;
    const show = () => panel.scrollIntoView({ behavior: "smooth", block: "start" });
    show();
    const grown = new ResizeObserver(show);
    grown.observe(panel);
    const settled = setTimeout(() => grown.disconnect(), 1500);
    return () => {
      grown.disconnect();
      clearTimeout(settled);
    };
  }, [planRow?.branch_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = rows.filter((r) => {
    if (governorate && r.governorate !== governorate) return false;
    if (scope === "enrolled" && !r.token_prefix) return false;
    if (scope === "unenrolled" && r.token_prefix) return false;
    if (scope === "stale" && !isStale(r)) return false;
    if (query && !matchesBranch(searchable(r), query)) return false;
    return true;
  });

  // Filters narrow the list under the current page often enough that the
  // page has to be clamped rather than left pointing past the end.
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const pageRows = visible.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);
  /** Any filter change starts again at the first page. */
  const filter = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPage(0);
  };
  /** The pager sits under 25 rows, so a page turn has to bring the top of
   *  the table back. */
  const goToPage = (next: number) => {
    setPage(next);
    tableTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const issue = async (row: FleetRow) => {
    if (
      row.token_prefix &&
      !window.confirm(
        `${row.name} already has a token (${row.token_prefix}…). Issuing a new one stops the PC holding the old one until it is given the new token. Continue?`,
      )
    )
      return;
    setBusy(row.branch_id);
    setError(null);
    try {
      setIssued(await api.issueToken(row.branch_id));
      fleet.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const addBranch = async (event: FormEvent) => {
    event.preventDefault();
    setBusy("new");
    setError(null);
    try {
      const created = await api.createBranch({
        id: draft.id,
        name: draft.name,
        governorate: draft.governorate,
        area: draft.area,
        address: draft.address,
        latitude: null,
        longitude: null,
        plus_code: null,
        source_url: null,
        timezone: "Africa/Cairo",
        active: true,
        video_dir: null,
      });
      setDraft(EMPTY_BRANCH);
      setAdding(false);
      setQuery(created.name);
      setGovernorate("");
      setScope("all");
      setPage(0);
      fleet.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (row: FleetRow) => {
    if (!window.confirm(`Revoke ${row.name}'s token? Its PC stops syncing until it gets a new one.`))
      return;
    setBusy(row.branch_id);
    setError(null);
    try {
      await api.revokeToken(row.branch_id);
      fleet.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {issued && (
        <Panel
          title={`Token for ${rows.find((r) => r.branch_id === issued.branch_id)?.name ?? issued.branch_id}`}
          subtitle="Shown once. Paste it into the installer's Hub page on that branch's PC (or into C:\ProgramData\Footfall\hub.token). The hub keeps only a hash."
          actions={
            <button type="button" className={ui.btnQuiet} onClick={() => setIssued(null)}>
              Done
            </button>
          }
        >
          <div className="flex flex-wrap items-center gap-3">
            <code className="rounded-lg border border-border bg-bg px-3 py-2 text-sm select-all">
              {issued.token}
            </code>
            <CopyButton text={issued.token} label="Copy token" className={ui.btn} />
          </div>
        </Panel>
      )}

      <Panel
        title="Fleet"
        subtitle={`${enrolled} of ${rows.length} branches have a token${stale ? ` · ${stale} not seen for a day` : ""}`}
        actions={
          <div className="flex gap-2">
            <button type="button" className={ui.btnQuiet} onClick={fleet.reload}>
              Refresh
            </button>
            <button type="button" className={ui.btnQuiet} onClick={() => setAdding((v) => !v)}>
              {adding ? "Cancel" : "Add branch"}
            </button>
          </div>
        }
      >
        {error && <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>}

        {adding && (
          <form onSubmit={addBranch} className={`${ui.toolbar} mb-4 items-end`}>
            <label className={ui.field}>
              Name
              <input className={`${ui.input} min-w-[240px]`} required autoFocus value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value, id: slugify(e.target.value) })}
                placeholder="Sheikh Zayed – Hyper One Branch" />
            </label>
            <label className={ui.field}>
              Governorate
              <input className={ui.input} required list="fleet-governorates" value={draft.governorate}
                onChange={(e) => setDraft({ ...draft, governorate: e.target.value })} />
              <datalist id="fleet-governorates">
                {governorates.map((g) => <option key={g} value={g} />)}
              </datalist>
            </label>
            <label className={ui.field}>
              Area
              <input className={ui.input} value={draft.area}
                onChange={(e) => setDraft({ ...draft, area: e.target.value })} />
            </label>
            <label className={ui.field}>
              Address
              <input className={`${ui.input} min-w-[240px]`} value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
            </label>
            <label className={ui.field} title="Its id everywhere: footage folder, token, history. Cannot change later.">
              Id
              <input className={`${ui.input} font-mono text-xs`} required pattern="[a-z0-9][a-z0-9-]*"
                value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} />
            </label>
            <button type="submit" className={ui.btn} disabled={busy === "new"}>
              Add
            </button>
            <span className="text-xs text-muted">
              Then issue its token; its PC creates the same branch for itself from that token.
            </span>
          </form>
        )}

        <div ref={tableTop} className={`${ui.toolbar} mb-4`}>
          <Select
            aria-label="Governorate"
            value={governorate}
            onChange={(e) => filter(setGovernorate)(e.target.value)}
          >
            <option value="">All governorates</option>
            {governorates.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Show"
            value={scope}
            onChange={(e) => filter<Scope>(setScope)(e.target.value as Scope)}
          >
            <option value="all">All branches</option>
            <option value="enrolled">With a token</option>
            <option value="unenrolled">Without a token</option>
            <option value="stale">Not seen for a day</option>
          </Select>
          <BranchSearch
            className="min-w-[240px]"
            branches={rows.map(searchable)}
            value={query}
            onChange={filter(setQuery)}
            onSelect={(branch) => {
              // Picking a store shows exactly it, whatever the other filters said.
              setGovernorate("");
              setScope("all");
              filter(setQuery)(branch.name);
            }}
          />
          <span className="text-xs text-muted">{visible.length} shown</span>
        </div>

        <AsyncBlock loading={fleet.loading} error={fleet.error} empty={visible.length === 0}
          emptyMessage="No branches match.">
          <div className="overflow-x-auto">
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Token</th>
                  <th>Last seen</th>
                  <th>Version</th>
                  <th>Last day</th>
                  <th>Queue</th>
                  <th>Setup</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.branch_id} className={isStale(row) ? "text-danger" : ""}>
                    <td>
                      <div className="font-medium text-text">{row.name}</div>
                      <div className="text-xs text-muted">
                        {row.governorate} › {row.area || "—"}
                        {row.is_demo ? " · demo" : ""}
                      </div>
                    </td>
                    <td>
                      {row.token_prefix ? (
                        <span title={`Issued ${formatDateTime(row.token_created_at)} by ${row.token_created_by}`}>
                          <code>{row.token_prefix}…</code>
                        </span>
                      ) : (
                        <span className="text-muted">none</span>
                      )}
                    </td>
                    <td title={row.last_seen_at ?? ""}>{formatDateTime(row.last_seen_at)}</td>
                    <td title={row.last_runtime ?? ""}>{row.last_version ?? "—"}</td>
                    <td title={row.last_day_at ? `received ${formatDateTime(row.last_day_at)}` : ""}>
                      {row.last_day ?? "—"}
                      {row.days_received ? (
                        <span className="text-xs text-muted"> · {row.days_received} uploads</span>
                      ) : null}
                    </td>
                    <td>{row.last_pending ?? "—"}</td>
                    <td title={row.last_setup_at ? `received ${formatDateTime(row.last_setup_at)}` : ""}>
                      {hasSetup(row) ? (
                        <>
                          {row.cameras} camera(s)
                          <span className="text-xs text-muted">
                            {row.has_plan ? " · plan" : " · no plan"}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {hasSetup(row) && (
                        <button
                          type="button"
                          className={`${ui.btnQuiet} mr-1.5 px-2.5 py-1 text-xs ${
                            planId === row.branch_id ? ui.btnSelected : ""
                          }`}
                          onClick={() =>
                            setPlanId(planId === row.branch_id ? null : row.branch_id)
                          }
                        >
                          Plan
                        </button>
                      )}
                      <button
                        type="button"
                        className={`${ui.btnQuiet} px-2.5 py-1 text-xs`}
                        disabled={busy === row.branch_id}
                        onClick={() => issue(row)}
                      >
                        {row.token_prefix ? "New token" : "Issue token"}
                      </button>
                      {row.token_prefix && (
                        <button
                          type="button"
                          className={`${ui.btnDanger} ml-1.5 px-2.5 py-1 text-xs`}
                          disabled={busy === row.branch_id}
                          onClick={() => revoke(row)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-xs text-muted">
              <span>
                {current * PAGE_SIZE + 1}–{current * PAGE_SIZE + pageRows.length} of{" "}
                {visible.length}
              </span>
              <button
                type="button"
                className={`${ui.btnQuiet} px-2.5 py-1 text-xs`}
                disabled={current === 0}
                onClick={() => goToPage(current - 1)}
              >
                Previous
              </button>
              <span className="px-1">
                Page {current + 1} of {pageCount}
              </span>
              <button
                type="button"
                className={`${ui.btnQuiet} px-2.5 py-1 text-xs`}
                disabled={current >= pageCount - 1}
                onClick={() => goToPage(current + 1)}
              >
                Next
              </button>
            </div>
          )}
        </AsyncBlock>
      </Panel>

      {planRow && (
        <div ref={planPanel}>
          <BranchPlanPanel key={planRow.branch_id} row={planRow} onClose={() => setPlanId(null)} />
        </div>
      )}
    </div>
  );
}
